import anime from 'animejs';
import { durations } from 'components/menu/motion';
import scroll from 'core/scroll';
import tag from 'core/tag';
import { bindMethod } from 'helpers/bind';
import { EASE_OUT } from 'helpers/easings';
import Component from 'navigation/component/Component';
import mqStore from 'store/mqStore';
import store from 'store/store';
export default class Drawer extends Component {
    name;
    params;
    previousIsTabletPortrait;
    static itemsToWatch = ['a.product-card[data-event]:not([data-viewed]), .row-product-card[data-event]:not([data-viewed]) '];
    get translateAxis() {
        const isTabletPortrait = mqStore.tabletPortrait.get();
        return isTabletPortrait ? `translate${this.params.mobile.axis}` : `translate${this.params.desktop.axis}`;
    }
    motions = [];
    opened = false;
    _translating = false;
    get translating() {
        return this._translating;
    }
    set translating(value) {
        this._translating = value;
        this.el.classList.toggle('translating', value);
    }
    io = new IntersectionObserver(this.onItemInView.bind(this), { threshold: [0.5], root: this.el });
    watchTimeout = null;
    constructor(el, options) {
        super(el, options);
        this.previousIsTabletPortrait = mqStore.tabletPortrait.get();
    }
    initialized() {
        super.initialized();
        this.setInitialPanelState();
        this.onSwitchMq();
    }
    watch = () => {
        this.io.disconnect();
        const elementsToWatch = document.querySelectorAll(Drawer.itemsToWatch.join(','));
        elementsToWatch.forEach((el) => this.io.observe(el));
    };
    onItemInView(entries) {
        const lists = {};
        if (this.watchTimeout)
            clearTimeout(this.watchTimeout);
        this.watchTimeout = setTimeout(() => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    const el = entry.target;
                    const event = tag.getEvent(el);
                    if (event?.ecommerce) {
                        if (lists[event.ecommerce.item_list_name])
                            lists[event.ecommerce.item_list_name].push(event.ecommerce.items[0]);
                        else
                            lists[event.ecommerce.item_list_name] = [event.ecommerce.items[0]];
                        this.io.unobserve(el);
                        el.dataset.viewed = 'true';
                    }
                }
            }
            tag.triggerListTag(lists);
        }, 200);
    }
    setInitialPanelState() {
        this.updatePanelTranslation(this.getDirection(false)[1]);
    }
    bindEvents(add) {
        const method = bindMethod(add);
        if (this.refs.close)
            this.refs.close[method]('click', this.onClose);
        store.drawer.toggleListener(add, this.onDrawerChange);
        mqStore.tabletPortrait.listen(this.onSwitchMq);
        window[method]('keydown', this.onKeyDown);
    }
    onKeyDown = (e) => {
        if (store.drawer.get() === this.name) {
            if (e.key === 'Escape')
                store.drawer.set(null);
        }
    };
    onDrawerChange = (state) => {
        if (state !== null) {
            store.search.set(null);
            store.panel.set(null);
        }
        if (state === this.name) {
            scroll.lock();
            this.toggle(true);
        }
        else if (store.previousDrawer.get() === this.name || this.opened) {
            scroll.unlock();
            this.toggle(false);
        }
    };
    toggle(value, force = false) {
        this.cancelMotions();
        const isTabletPortrait = mqStore.tabletPortrait.get();
        const axis = isTabletPortrait ? this.params.mobile.axis : this.params.desktop.axis;
        const resetAxis = isTabletPortrait ? `translate${axis}` : `translate${axis}`;
        anime.set(this.el, { [resetAxis]: 0 });
        const m = anime({
            targets: this.el,
            [this.translateAxis]: {
                value: this.getDirection(value),
                duration: value ? durations.FIRST_PANEL_TRANSLATE_IN : durations.FIRST_PANEL_TRANSLATE_OUT,
                easing: value ? EASE_OUT : 'easeInSine'
            },
            opacity: {
                value: value ? [0, 1] : [1, 0],
                duration: value ? durations.FIRST_PANEL_OPACITY_IN : durations.FIRST_PANEL_OPACITY_OUT,
                easing: 'linear',
                delay: value ? 0 : (durations.FIRST_PANEL_OPACITY_OUT) / 2
            },
            begin: () => {
                this.translating = true;
                this.onBeginToggle(value);
                this.el.classList.toggle('opened', value);
            },
            complete: () => {
                this.translating = false;
                this.opened = value;
                this.onEndToggle(value);
                if (value)
                    this.watch();
                else
                    this.io.disconnect();
            }
        });
        this.motions.push(m);
        if (axis === 'X') {
            const m1 = anime({
                targets: this.el.children[0],
                opacity: {
                    value: value ? [0, 1] : [1, 0],
                    duration: value ? durations.FIRST_PANEL_OPACITY_IN : durations.FIRST_PANEL_OPACITY_OUT / 2,
                    easing: 'linear'
                }
            });
            this.motions.push(m1);
        }
    }
    onBeginToggle(value) { }
    onEndToggle(value) { }
    onClose = () => {
        if (!store.persistantOverlay.get())
            store.drawer.set(null);
    };
    onSwitchMq = () => {
        const isTabletPortrait = mqStore.tabletPortrait.get();
        this.el.classList.toggle('drawer__classic', this.params[isTabletPortrait ? 'mobile' : 'desktop'].direction === 'classic');
        this.el.classList.toggle('drawer__reversed', this.params[isTabletPortrait ? 'mobile' : 'desktop'].direction === 'reversed');
        if (isTabletPortrait !== this.previousIsTabletPortrait) {
            const currentState = store.drawer.get() === this.name;
            this.resetNonActiveAxis();
            this.previousIsTabletPortrait = isTabletPortrait;
            this.updatePanelTranslation(this.getDirection(currentState)[1]);
        }
    };
    updatePanelTranslation(translateValue) {
        anime.set(this.el, { [this.translateAxis]: translateValue });
    }
    resetNonActiveAxis() {
        const inactiveAxis = this.translateAxis === 'translateX' ? 'translateY' : 'translateX';
        anime.set(this.el, { [inactiveAxis]: 0 });
    }
    getDirection(opened) {
        const isTabletPortrait = mqStore.tabletPortrait.get();
        const direction = isTabletPortrait ? this.params.mobile.direction : this.params.desktop.direction;
        if (direction === 'classic')
            return opened ? ['-100%', '0'] : ['0', '-100%'];
        else
            return isTabletPortrait ? (opened ? ['100%', '0'] : ['0', '100%']) : (opened ? ['50%', '0'] : ['0', '50%']);
    }
    cancelMotions() {
        this.motions.forEach((m) => m.pause());
        this.motions = [];
    }
    resize() {
        super.resize();
    }
}
